import {
  TransactionsViewDocument,
  useCreateShareTransactionGroupMutation,
  useTransactionsViewSuspenseQuery,
} from '../transactions-view.graphql';
import React, { Suspense, useCallback } from 'react';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { orderBy, toNumber } from 'lodash-es';
import { CreateShelfCompanyForm, SuggestShelfCompanySetup } from './CreateShelfCompanyForm';
import { CreateShareTransactionGroupForm, type StgFormData } from './TransactionGroupForm';
import { EditSharesAtFoundation } from './EditSharesAtFoundation';
import { DateTime } from 'luxon';
import { BigNumber } from 'bignumber.js';
import { GqlTransactionKind } from '../../../../graphql/base-types.graphql';
import { Box } from '@startuptools/ui/Box';
import { ErrorBoundary } from '@startuptools/ui/ErrorBoundary';
import { Skeleton } from '@startuptools/ui/Skeleton';
import { Stack } from '@startuptools/ui/Stack';
import { SideNavDocument } from '../../../../components/side-nav/side-nav.graphql';
import { useNavigate } from '../../../../components/Navigate';

type Props = Omit<
  React.ComponentProps<typeof CreateShareTransactionGroupForm>,
  'company' | 'shareTransactionGroups' | 'snrShareCapitalChanges' | 'save' | 'loading' | 'error' | 'close'
> & { close: (goToNextGroup?: boolean) => void };

enum State {
  SUGGESTED = 'suggested',
  STARTED = 'started',
  SKIPPED = 'skipped',
  CONFIGURE_SHARE_COUNT = 'configure_share_count',
}

export const TransactionGroupDialog = ({ close, ...rest }: Props) => {
  const companyId = useCompanyId();
  const {
    data: { company, nextShareCapitalChanges, shareTransactionGroups, snrShareCapitalChanges: snr },
  } = useTransactionsViewSuspenseQuery({ variables: { companyId }, fetchPolicy: 'cache-and-network' });

  const snrShareCapitalChanges = orderBy(
    snr,
    [e => toNumber(e.filingYear), e => toNumber(e.filingNumber), e => toNumber(e.serialNumber)],
    ['asc', 'asc', 'asc'],
  );

  const { next, prev, afterNext } = nextShareCapitalChanges;

  const foundationShareCountMissing = next && !next.changedNumberOfShares && !shareTransactionGroups.length;
  const foundedAsShelfCompany = company.transferDate && !shareTransactionGroups.length;

  const [state, setState] = React.useState<State>(
    foundationShareCountMissing ? State.CONFIGURE_SHARE_COUNT : foundedAsShelfCompany ? State.SUGGESTED : State.SKIPPED,
  );

  const { navigate } = useNavigate();

  const [_, startTransition] = React.useTransition();

  const [create, { loading, error }] = useCreateShareTransactionGroupMutation({
    refetchQueries: [TransactionsViewDocument, SideNavDocument],
  });

  const submit = useCallback(
    async (data: StgFormData) => {
      const isAddingSplit = [GqlTransactionKind.Split, GqlTransactionKind.Merge].includes(
        data.kind === 'next' ? next!.kind : data.kind,
      );
      const transactionDate = data.date.toUTC(0, { keepLocalTime: true }).toISO();
      if (!transactionDate) {
        throw new Error('no date found');
      }
      const splitFactor = isAddingSplit ? new BigNumber(data.sharesAfter).div(data.sharesBefore).toString() : undefined;
      if (splitFactor === 'NaN') {
        throw new Error('Invalid splitFactor is NaN');
      }
      const doThis = async () => {
        const response = await create({
          variables: {
            companyId,
            input: {
              kind: data.kind === 'next' ? next!.kind : data.kind,
              transactionDate,
              beforeStgId: data.beforeStgId || null,
              snrShareCapitalChangeId: data.kind === 'next' ? next!.sccId : undefined,
              splitFactor: isAddingSplit
                ? new BigNumber(data.sharesAfter).div(data.sharesBefore).toString()
                : undefined,
              roundupShareholderId: data.roundUpShareholderId,
            },
          },
        });
        if (isAddingSplit || next?.kind === GqlTransactionKind.CurrencyChange) {
          return close(Boolean(response?.data?.createShareTransactionGroup.next));
        }
        if (response.data?.createShareTransactionGroup) {
          void navigate(
            `/share-register/transactions/${response.data.createShareTransactionGroup.transactions[0]?.id}`,
          );
        }
        close();
      };
      startTransition(() => {
        void doThis();
      });
    },
    [close, companyId, create, next, navigate],
  );

  if (state === State.CONFIGURE_SHARE_COUNT) {
    return (
      <EditSharesAtFoundation
        close={() => close(false)}
        onSave={() => {
          setState(State.SKIPPED);
        }}
      />
    );
  }
  if (state === State.SUGGESTED) {
    return (
      <SuggestShelfCompanySetup
        transferDate={DateTime.fromISO(company.transferDate!)}
        close={() => close(false)}
        startShelfCompanySetup={() => {
          setState(State.STARTED);
        }}
        startRegularSetup={() => {
          setState(State.SKIPPED);
        }}
      />
    );
  }
  if (state === State.STARTED) {
    return <CreateShelfCompanyForm company={company} close={() => close(false)} />;
  }

  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          <Stack gap={3} m={3}>
            <Skeleton variant="text" level="h3" />
            <Box>
              <Skeleton variant="text" sx={{ maxWidth: 50 }} />
              <Skeleton variant="text" level="body-md" />
              <Skeleton variant="text" level="body-md" sx={{ maxWidth: 200 }} />
              <Skeleton variant="text" level="body-md" sx={{ maxWidth: 200 }} />
              <Skeleton variant="text" level="body-md" sx={{ maxWidth: 200 }} />
            </Box>
            <Box>
              <Skeleton variant="text" sx={{ maxWidth: 50 }} />
              <Skeleton variant="rectangular" height={40} />
            </Box>
            <Stack direction="row" justifyContent="end" alignItems="center" gap={1}>
              <Skeleton variant="rectangular" height={36} width={80} />
              <Skeleton variant="rectangular" height={36} width={80} />
            </Stack>
          </Stack>
        }
      >
        <CreateShareTransactionGroupForm
          {...rest}
          save={submit}
          loading={loading}
          error={error}
          close={() => close(false)}
          next={next}
          prev={prev}
          afterNext={afterNext}
          company={company}
          shareTransactionGroups={shareTransactionGroups}
          snrShareCapitalChanges={snrShareCapitalChanges}
        />
      </Suspense>
    </ErrorBoundary>
  );
};
